import React from "react";
import { toJS } from "mobx";
import { useObserver } from "mobx-react";
import {
  DialogActions,
  Grid,
  makeStyles,
  Typography,
  alpha,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel
} from "@material-ui/core";
import { Form } from "@modules/formx-react";
import { useNavigation } from "@navigation";
import { FullTravelRequestFragment, AccommodationTypeOf } from "@graphql";
import { useLocale } from "@hooks";
import { PrimaryButton, RegularButton, Spinner } from "@components";
import { FormxDebugger, RadioGroup, Text } from "@components/formx";
import { LuggageType, TransportationType } from "@bonliva-crm/shared";
import { TravelRequestRideSection } from "./TravelRequestRideSection";
import { useTravelRequestForm } from "./useTravelRequestForm";
import { ConsultantSelector } from "@containers/VirtualSelector/ConsultantSelector";
import { ProjectSelector } from "@bookings/ProjectSelector/ProjectSelector";
import Select from "@components/Select";
import TicketCategories from "@tickets/TicketCreate/ticket-categories";

type Props = {
  bookingGroupId?: string;
  readonly: boolean;
  initialData: FullTravelRequestFragment | null;
  isNew: boolean;
};

export function TravelRequestForm(props: Props) {
  const nav = useNavigation();
  const { locale } = useLocale();

  const isNew = props.isNew;

  const {
    form,
    localStore,
    projectSelectorStore,
    addRide,
    removeRide,
    setConsultant,
    setCostCenter,
    setIsReschedule,
    consultantValue,
    costCenterOptions,
    consultantError,
    projectError,
    setTicketDifficultyLevel,
    ticketDifficultyLevelError,
    ticketDifficultyLevelOptions
  } = useTravelRequestForm(
    props.bookingGroupId,
    props.initialData,
    props.readonly,
    isNew
  );

  const classes = useStyles({});

  return useObserver(() => {
    nav.lockNavigation("travel_request", form.dirty);

    if (localStore.loading) {
      return <Spinner fullHeight />;
    }

    const rides = form.values.rides;
    const isFlight = rides.some((ride) =>
      ride.transportationType?.includes(TransportationType.Flight)
    );

    const isBookingGroupContext = !!props.bookingGroupId;

    const renderConsultantAndProjectFields = () => (
      <>
        <Grid item xs={12}>
          <ConsultantSelector
            fullWidth
            required
            disabled={props.readonly}
            label={locale.Consultants}
            onChange={setConsultant}
            value={consultantValue}
            error={consultantError}
          />
        </Grid>
        <Grid item xs={12}>
          <ProjectSelector
            clearable
            ProjectSelectProps={{ disabled: props.readonly, required: true }}
            store={projectSelectorStore}
            value={form.values.projectId}
            error={projectError}
            onChange={(v) => form.setValue("projectId", v)}
          />
        </Grid>
        <Grid item xs={12}>
          <Select
            name="costCenterId"
            fullWidth
            disabled={props.readonly}
            label={locale.CostCenter}
            options={costCenterOptions}
            value={form.values.costCenterId}
            onChange={setCostCenter}
            error={!!form.getError("costCenterId")}
          />
        </Grid>
      </>
    );

    return (
      <div className={classes.travelRequestForm}>
        <Form store={form} allowUpdates>
          <FormxDebugger store={form} />

          <Grid item xs={12} container spacing={3}>
            {!isBookingGroupContext && renderConsultantAndProjectFields()}

            <Grid item xs={12} sm={8} lg={5} container>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={form.values.isReschedule}
                      onChange={(e) => setIsReschedule(e.target.checked)}
                      disabled={props.readonly}
                    />
                  }
                  label={locale.travelRequest.isReschedule}
                />
              </Grid>

              {form.values.isReschedule && (
                <Grid item xs={12}>
                  <Text
                    name="rescheduleComment"
                    label={locale.travelRequest.rescheduleComment}
                    TextFieldProps={{
                      required: true,
                      disabled: props.readonly
                    }}
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              {rides.map((ride, i) => (
                <TravelRequestRideSection
                  key={i}
                  name={`rides.${i}`}
                  addRide={() => addRide(i)}
                  removeRide={() => removeRide(i)}
                  ride={toJS(ride)}
                  allowToAddRide={rides.length < 2}
                  readonly={props.readonly}
                  disabled={form.disabled}
                  countries={localStore.allCountriesOptions}
                />
              ))}
            </Grid>

            {isFlight && (
              <>
                {/* Only show luggage field if it's an old travel request */}
                {!isNew && form.values.luggage ? (
                  <Grid item xs={12}>
                    <Text name="luggage" label={locale.travelRequest.luggage} />
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <RadioGroup
                    name="luggageType"
                    label={locale.travelRequest.luggageTypeLabel}
                    LabelProps={{ required: true }}
                    noLabelMargin
                    options={[
                      {
                        label: locale.luggageType[LuggageType.Handluggage],
                        value: LuggageType.Handluggage
                      },
                      {
                        label: (
                          <>
                            {locale.luggageType[LuggageType.HandluggagePlusChecked]}
                            <Typography
                              component="div"
                              variant="caption"
                              color="textSecondary"
                              className={classes.extraCostNote}
                            >
                              {locale.travelRequest.extraCostNote}
                            </Typography>
                          </>
                        ),
                        value: LuggageType.HandluggagePlusChecked
                      }
                    ]}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <RadioGroup
                name="isCancelable"
                noLabelMargin
                label={locale.travelRequest.isCancelable}
                LabelProps={{ required: true }}
                disabled={form.disabled}
                row
                options={locale.yesNoOptions}
              />
            </Grid>

            <Grid item xs={12}>
              <RadioGroup
                noLabelMargin
                name="isFlexible"
                label={locale.travelRequest.isFlexible}
                LabelProps={{ required: true }}
                disabled={form.disabled}
                row
                options={locale.yesNoOptions}
              />
            </Grid>

            <Grid item xs={12} sm={7} lg={5}>
              <FormControl>
                <FormLabel required error={ticketDifficultyLevelError}>
                  {locale.ticketDifficultyLevel.title}
                </FormLabel>
                <Typography variant="body2" color="textSecondary">
                  {locale.ticketDifficultyLevel.subtitle}
                </Typography>
                <Select
                  name="ticketDifficultyLevel"
                  required
                  clearable={false}
                  fullWidth
                  className={classes.ticketDifficultyLevelSelect}
                  disabled={form.disabled}
                  value={form.values.ticketDifficultyLevel}
                  options={ticketDifficultyLevelOptions}
                  onChange={(value) => setTicketDifficultyLevel(value)}
                  error={ticketDifficultyLevelError}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TicketCategories selected={form.values.ticketDifficultyLevel} />
            </Grid>

            <Grid item xs={12}>
              <Text name={"travelPreferences"} label={locale.travelRequest.others} />
            </Grid>

            <Grid item xs={12} className={classes.travelSuggestion}>
              <RadioGroup
                name="travelSuggestion"
                noLabelMargin
                row
                LabelProps={{ required: true }}
                TextFieldProps={{ error: false }}
                label={locale.travelRequest.shouldBeBookedDirectly}
                options={Object.values(AccommodationTypeOf).toLabelValue(
                  (x) => locale.accommodationTypeOf[x]
                )}
              />
            </Grid>
          </Grid>

          {!props.readonly && (
            <DialogActions style={{ padding: "16px 0" }}>
              <RegularButton style={{ marginLeft: "auto" }} onClick={() => nav.close()}>
                {locale.Cancel}
              </RegularButton>
              <PrimaryButton
                disabled={!form.canSubmit}
                loading={form.isSubmitting}
                onClick={form.submit}
              >
                {locale.Send}
              </PrimaryButton>
            </DialogActions>
          )}
        </Form>
      </div>
    );
  });
}

const useStyles = makeStyles((theme) => ({
  travelRequestForm: {
    "& label.Mui-required": {
      color: `${alpha(theme.palette.common.black, 0.54)}`
    },
    "& label.Mui-required > span": {
      color: `${theme.palette.error.main}`
    },
    "& .Mui-error:after, & .react-select-mui:after": {
      transform: "scaleX(0)",
      borderBottom: `1px solid ${theme.palette.common.black}`
    }
  },
  transportationType: {
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2)
  },
  radioGroup: {
    flexDirection: "row",
    paddingLeft: theme.spacing(2)
  },
  travelSuggestion: {
    marginTop: theme.spacing(2)
  },
  extraCostNote: {
    marginTop: "-0.2rem"
  },
  ticketDifficultyLevelSelect: {
    width: 200
  }
}));
